import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import { HqMaterials } from "./hqMaterials";
import { buildHoleSheet, type HoleSheet } from "./holeSheet";
import { safeNormals, vnoise, warp } from "./groundNoise";
import { GroundMaterials } from "./groundMaterials";
import type { Maps } from "./groundMaps";

/**
 * The holes in the ground (groundMesh.ts's fourth layer): the melt in the lava basins and their baked glow discs, the
 * chasm floors, and the mist pooled in the dry gorges.
 */
export class GroundHoles extends GroundMaterials {
  /** Quads per cell side on the chasm floor and the melt. */
  static readonly HoleSub = 3;
  /**
   * How far the floor and the melt run in under the ground past a wall's edge. The wall at a lip wanders up to 3.1 ×
   * `Warp` and the floor 1 × (at most 0.42 apart) while the wall's batter carries its foot ~0.27 back into the hole;
   * this clears the rest with room to spare and stays short of the next hole across a one-cell strip of ground.
   */
  static readonly HoleSkirt = 0.55;

  /** The mist sheets: height over the floor, how far short of a wall each fades out, and its share of `MistAlpha`. */
  static readonly MistLayers: readonly [number, number, number][] = [[0.5, 0.3, 1], [1.05, 0.18, 0.6]];
  static readonly MistAlpha = 0.42;

  /**
   * Most glow discs the ground and cliff shaders loop over; past this the smallest go dark (the melt itself still
   * glows and blooms). Eight covers every authored ashlands level but one once the rivers are split into pieces.
   */
  static readonly MaxLavaSpots = 8;
  /**
   * A basin's glow is split into discs spanning at most this many cells from one end, so a lava river lights its own
   * banks. As ONE disc, level 45's 21-cell river had a radius of ten cells: the glow lit everything within six of
   * its middle at full strength, and measured its height from the melt there, so the lower bench round the fortress —
   * nearer that melt level than the banks — washed flat orange, cobbles and all.
   */
  static readonly GlowChunkSpan = 3;
  /**
   * The molten surface in every lava basin.
   *
   * The old kit drew lava as a flat model sitting on the ground: a dark square with orange lines painted on it and no
   * depth whatsoever. Here the ground genuinely opens — the cliff band wraps the basin down to `LavaLevel` — and this
   * mesh is the melt at the bottom of it. Crust plates drift apart on a slow flow field and glow in the cracks
   * between them, pushed past 1 so the bloom pass catches it.
   */
  protected buildLava(): THREE.Mesh | null {
    const cells: Cell[] = [];
    for (let i = 0; i < this.grid.cellCount; i++) {
      const c = this.grid.cellAt(i);
      if (this.terrain.isLava(c)) cells.push(c);
    }
    if (cells.length === 0) return null;
    const sheet = this.holeSheet(cells, (x, z) => this.terrain.isLava(new Cell(x, z)),
      (x, z) => this.terrain.lavaAt(x, z), (x, z) => (vnoise(x * 3.1, z * 3.1) - 0.5) * 0.05);
    const pos = sheet.position, uv = sheet.xz, idx = sheet.index;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    safeNormals(g);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: `varying vec2 vP; void main() { vP = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `varying vec2 vP; uniform float uTime;
        float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float n(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f); return mix(mix(h(i), h(i + vec2(1, 0)), f.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), f.x), f.y); }
        float fbm(vec2 p) { return n(p) * 0.5 + n(p * 2.1 + 3.7) * 0.28 + n(p * 4.3 + 9.1) * 0.14 + n(p * 8.7 + 1.1) * 0.08; }
        vec2 plates(vec2 p, out float edge) {
          vec2 ip = floor(p), fp = fract(p);
          float best = 9.0, second = 9.0; vec2 id = vec2(0.0);
          for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
            vec2 o = vec2(float(i), float(j));
            vec2 c = o + vec2(h(ip + o), h(ip + o + 37.0));
            float d = length(c - fp);
            if (d < best) { second = best; best = d; id = ip + o; }
            else if (d < second) second = d;
          }
          edge = second - best;
          return id;
        }
        void main() {
          vec2 flow = vec2(uTime * 0.012, uTime * 0.007);
          vec2 p = vP * 13.0 + flow + vec2(fbm(vP * 4.0 + flow * 2.0), fbm(vP * 4.0 + 11.0)) * 1.4;
          float edge;
          vec2 id = plates(p, edge);
          float crack = 1.0 - smoothstep(0.0, 0.24, edge);
          float grain = fbm(p * 5.5);
          float plateAge = h(id);
          vec3 crust = mix(vec3(0.055, 0.042, 0.045), vec3(0.16, 0.13, 0.13), plateAge * 0.6 + grain * 0.4);
          crust *= 0.7 + 0.6 * grain;
          float pulse = 0.72 + 0.28 * sin(uTime * 1.6 + id.x * 2.3 + id.y * 1.7);
          float hot = crack * pulse;
          vec3 melt = mix(vec3(1.7, 0.40, 0.05), vec3(2.7, 1.30, 0.28), smoothstep(0.45, 1.0, hot));
          vec3 col = mix(crust, melt, smoothstep(0.04, 0.42, hot));
          col += vec3(0.7, 0.2, 0.03) * smoothstep(0.5, 0.0, edge) * 0.7;
          col += vec3(0.34, 0.08, 0.01) * smoothstep(0.55, 0.15, grain);
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    const mesh = new THREE.Mesh(g, mat);
    mesh.receiveShadow = false;
    mesh.castShadow = false;
    mesh.renderOrder = 1;
    // Emissive colour alone lights nothing — it only feeds the bloom pass. This used to add a warm point light per
    // few cells, up to eight a level, and every lit fragment in the scene loops over every point light (and adding
    // one recompiles every material). The glow onto the lip and the walls is baked into the ground and cliff shaders
    // now (`lavaGlow`, from `lavaSpots`); ONE light remains, over the biggest basin, for the props and units that
    // stand near it.
    const big = this.lavaSpots.reduce<THREE.Vector4 | null>((a, b) => (!a || b.z > a.z ? b : a), null);
    if (big) {
      const lamp = new THREE.PointLight(0xff6a1e, 3.2, 4.5 + big.z * 1.5, 2);
      lamp.position.set(big.x, big.w + 0.35, big.y);
      mesh.add(lamp);
    }
    (mesh as THREE.Mesh & { tick?: (t: number) => void }).tick = (t: number) => { mat.uniforms.uTime.value = t; };
    return mesh;
  }

  /**
   * The rubble floor at the bottom of every chasm.
   *
   * Without it a chasm is a hole straight through the world and you see whatever is behind it — which used to be the
   * haze backstop plane, capping the gorge with a flat sheet. A floor closes it, and it means the backstop can sit
   * just under the ground where it belongs instead of far below every gorge.
   */
  protected buildPitFloor(m: Maps): THREE.Mesh | null {
    const cells: Cell[] = [];
    for (let i = 0; i < this.grid.cellCount; i++) {
      const c = this.grid.cellAt(i);
      if (this.terrain.isPit(c)) cells.push(c);
    }
    if (cells.length === 0) return null;
    const sheet = this.holeSheet(cells, (x, z) => this.terrain.isPit(new Cell(x, z)),
      (x, z) => this.terrain.floorAt(x, z), (x, z) => (vnoise(x * 2.7, z * 2.7) - 0.5) * 0.12);
    const pos = sheet.position, idx = sheet.index;
    // denser than the surface: at one repeat per four units the rock read as huge flat diamonds
    const uv = sheet.xz.map((v) => v * 0.85);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    safeNormals(g);
    // soil, not rock: the bottom of a gorge is what has fallen into it, and the rock map's bedding reads as a tiled
    // floor from directly above
    const mat = new THREE.MeshStandardMaterial({ map: m.soil.alb, normalMap: m.soil.nrm, metalness: 0, roughness: 0.95 });
    mat.color.setScalar(0.30);                                 // deep in its own shadow
    mat.envMapIntensity = GroundMaterials.EnvIntensity;
    this.floorFog(mat);
    const mesh = new THREE.Mesh(g, mat);
    mesh.receiveShadow = true;
    return mesh;
  }

  /**
   * The welded floor or melt across every cell of one kind of hole, reaching in under the walls round it
   * (holeSheet.ts). Its cells sit at their centre height — the height `floorBeside` builds the wall's foot down to.
   */
  protected holeSheet(cells: Cell[], has: (x: number, z: number) => boolean, levelOf: (x: number, z: number) => number,
    bump: (x: number, z: number) => number): HoleSheet {
    return buildHoleSheet({
      cells, has, levelOf, bump,
      skirtToward: (x, z) => this.land.has(`${x}|${z}`),
      sub: GroundHoles.HoleSub,
      skirt: GroundHoles.HoleSkirt,
      // the plain wander, not `warpAt`: that one jumps from ×3.1 to ×1 between a lip cell and the hole, and at a
      // third of a cell per quad the jump can fold a triangle over (culled: another hole). The skirt absorbs the
      // difference from the walls' own wander.
      warp,
    });
  }
  /**
   * Mist pooled in the dry gorges (Phase 2, the gorge crossing; terrain F8): two soft sheets per gorge, low over the
   * floor, whose value-noise alpha drifts slowly with the wind. Seen from above, a chasm that fills with haze toward
   * its bottom reads as DEPTH in a way no amount of wall texture does.
   *
   * Transparent sheets cut a hard line where they meet a wall, and there is no depth texture on the phone to soften
   * that, so each sheet fades to nothing short of the walls instead: its alpha runs to zero before the cell edge on
   * every side whose neighbour is ground (the walls batter inward as they fall, so the lower sheet fades sooner).
   * Cells meet exactly at their edges — no overlap, which would double the alpha in seams. Fill cost is the gorge's
   * screen area twice, at alpha ≤ 0.42.
   */
  protected buildPitMist(): THREE.Mesh | null {
    const pits: Cell[] = [];
    for (let i = 0; i < this.grid.cellCount; i++) { const c = this.grid.cellAt(i); if (this.terrain.isPit(c)) pits.push(c); }
    if (pits.length === 0) return null;
    const N = 4, pos: number[] = [], edge: number[] = [], idx: number[] = [];
    for (const [lift, clear, weight] of GroundHoles.MistLayers) {
      for (const c of pits) {
        const open = (dx: number, dz: number) => this.terrain.isPit(new Cell(c.x + dx, c.y + dz));
        const px = open(1, 0), nx = open(-1, 0), pz = open(0, 1), nz = open(0, -1);
        const base = pos.length / 3;
        for (let iz = 0; iz <= N; iz++) {
          for (let ix = 0; ix <= N; ix++) {
            const ox = -0.5 + ix / N, oz = -0.5 + iz / N;
            const wx = c.x + ox, wz = c.y + oz;
            const [dwx, dwz] = this.warpAt(wx, wz);
            pos.push(wx + dwx, this.terrain.floorAt(wx, wz) + lift, wz + dwz);
            // 1 in the open middle, 0 by `clear` short of each wall
            const f = (d: number, isOpen: boolean) => (isOpen ? 1 : Math.min(1, Math.max(0, (0.5 - clear - d) / 0.3)));
            edge.push(f(ox, px) * f(-ox, nx) * f(oz, pz) * f(-oz, nz) * weight);
          }
        }
        for (let iz = 0; iz < N; iz++) for (let ix = 0; ix < N; ix++) {
          const a = base + iz * (N + 1) + ix, b = a + 1, d = a + (N + 1), e = d + 1;
          idx.push(a, d, b, b, d, e);
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("aEdge", new THREE.Float32BufferAttribute(edge, 1));
    g.setIndex(idx);
    const [wdx, wdz] = GroundMaterials.WindDir;
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: HqMaterials.time, uHaze: GroundMaterials.haze },
      vertexShader: /* glsl */ `
        attribute float aEdge; varying float vEdge; varying vec2 vXZ;
        void main() { vEdge = aEdge; vXZ = position.xz; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform vec3 uHaze; varying float vEdge; varying vec2 vXZ;
        float mh(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float mn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(mh(i), mh(i + vec2(1, 0)), f.x), mix(mh(i + vec2(0, 1)), mh(i + vec2(1, 1)), f.x), f.y); }
        void main() {
          vec2 drift = vec2(${wdx.toFixed(2)}, ${wdz.toFixed(2)}) * uTime * 0.06;
          float n = mn(vXZ * 1.1 + drift) * 0.6 + mn(vXZ * 2.7 - drift * 1.7 + 5.0) * 0.4;
          float a = smoothstep(0.25, 0.8, n) * ${GroundHoles.MistAlpha.toFixed(2)} * vEdge;
          gl_FragColor = vec4(uHaze * vec3(0.86, 0.9, 1.0), a);
        }`,
      transparent: true, depthWrite: false,
    });
    const mesh = new THREE.Mesh(g, mat);
    mesh.renderOrder = 1;
    mesh.castShadow = mesh.receiveShadow = false;
    return mesh;
  }
  /**
   * The level's lava basins as glowing discs: (centre x, centre z, radius, melt y). A basin is a connected clump of
   * lava cells, cut into pieces `GlowChunkSpan` across; each disc's radius reaches its farthest cell's edge.
   */
  protected lavaClusters(): THREE.Vector4[] {
    const lava: Cell[] = [];
    for (let i = 0; i < this.grid.cellCount; i++) { const c = this.grid.cellAt(i); if (this.terrain.isLava(c)) lava.push(c); }
    const seen = new Set<string>(), out: THREE.Vector4[] = [];
    for (const start of lava) {
      if (seen.has(`${start.x}|${start.y}`)) continue;
      const clump: Cell[] = [], stack = [start];
      seen.add(`${start.x}|${start.y}`);
      while (stack.length) {
        const c = stack.pop()!;
        clump.push(c);
        for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const n = new Cell(c.x + dx, c.y + dz), k = `${n.x}|${n.y}`;
          if (!seen.has(k) && this.terrain.isLava(n)) { seen.add(k); stack.push(n); }
        }
      }
      for (const part of glowChunks(clump, GroundHoles.GlowChunkSpan)) {
        const cx = part.reduce((a, c) => a + c.x, 0) / part.length, cz = part.reduce((a, c) => a + c.y, 0) / part.length;
        const r = Math.max(...part.map((c) => Math.hypot(c.x - cx, c.y - cz))) + 0.6;
        out.push(new THREE.Vector4(cx, cz, r, this.terrain.lavaAt(cx, cz)));
      }
    }
    return out.sort((a, b) => b.z - a.z).slice(0, GroundHoles.MaxLavaSpots);
  }
}

/**
 * Cuts a clump of cells into pieces at most `span` cells across: repeatedly the remaining cell farthest from the rest's
 * centre (an end of a river, a corner of a pool) and everything within `span` of it. A compact pool stays one piece.
 */
export function glowChunks(cells: readonly Cell[], span: number): Cell[][] {
  let left = [...cells];
  const out: Cell[][] = [];
  while (left.length) {
    const cx = left.reduce((a, c) => a + c.x, 0) / left.length, cz = left.reduce((a, c) => a + c.y, 0) / left.length;
    const seed = left.reduce((a, c) => (Math.hypot(c.x - cx, c.y - cz) > Math.hypot(a.x - cx, a.y - cz) ? c : a));
    const near = (c: Cell) => Math.max(Math.abs(c.x - seed.x), Math.abs(c.y - seed.y)) <= span;
    out.push(left.filter(near));
    left = left.filter((c) => !near(c));
  }
  return out;
}
